<template>
    <div>
        <!-- Layername for NedGlobeServices -->
        <x-input
            v-show="isOwsNedGlobeServices"
            :id="'themelayer-name'"
            :name="'themelayer-name'"
            :text="$t('labelLayerName')"
            :plaintext="isEditMode"
            :value="localPropertyValues.layerName"
            @input="$_onInputNedGlobeServicesLayerName"
            :invalidMessage="localStateValues.nameInvalidMessage"
            :state="localStateValues.nameValidState"
            :placeholder="$t('nedglobeServices.placeholderLayerName')"
            :maxlength="50"
            :required="isOwsNedGlobeServices"
        >
        </x-input>
        <!-- Title -->
        <x-input
            :id="'themelayer-title'"
            :name="'themelayer-title'"
            :text="$t('labelThemeLayerTitle')"
            :value="localPropertyValues.layerCustomTitle"
            @input="$_onInputTitle"
            :invalidMessage="localStateValues.titleInvalidMessage"
            :state="localStateValues.titleValidState"
            :placeholder="$t('placeholderThemeLayerTitle')"
            :maxlength="50"
            :required="true"
        >
        </x-input>
        <!-- IsPubliclyAccessible -->
        <x-checkbox
            v-show="isOwsNedGlobeServices"
            :id="'publicly-accessable-id'"
            :name="'publicly-accessable-name'"
            class="mb-3"
            :text="$t('labelThemelayerIsPubliclyAccessible')"
            :value="isPubliclyAccessible"
            @input="$_onIsPubliclyAccessible"
        >
        </x-checkbox>
        <!-- GeometryType for NedGlobeServices -->
        <b-form-group
            v-show="isOwsNedGlobeServices && isNedGlobeServicesLayerSourceTable"
            :label="$t('geomType')"
        >
            <b-form-checkbox-group
                v-model="geometryTypeArray"
                @input="$_onGeometryType"
            >
                <b-form-checkbox
                    name="chkGeoTypePoint"
                    :value="FEEDBACKS_GEOMETRY_TYPE_CODES.POINT"
                >
                    {{ $t('geomPoint') }}
                </b-form-checkbox>
                <b-form-checkbox
                    name="chkGeoTypeLine"
                    :value="FEEDBACKS_GEOMETRY_TYPE_CODES.LINE"
                >
                    {{ $t('geomLine') }}
                </b-form-checkbox>
                <b-form-checkbox
                    name="chkGeoTypePolygon"
                    :value="FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON"
                >
                    {{ $t('geomPolygon') }}
                </b-form-checkbox>
            </b-form-checkbox-group>
        </b-form-group>
        <!-- Selection -->
        <div
            v-if="
                isOWSLayerWFSSelected ||
                (isOwsNedGlobeServices && !isNedGlobeServicesLayerSourceLufo)
            "
        >
            <!-- Selection tolerance -->
            <x-select
                :id="'selection-tolerance-id'"
                :name="'selection-tolerance-name'"
                :text="$t('labelSelectionTolerance')"
                :value="
                    localPropertyValues.toleranceType
                        ? localPropertyValues.toleranceType.value
                        : null
                "
                :options="toleranceTypes"
                :disabled="$_hasPointOrLineGeometry"
                @selected="$_onSelectionTolerance"
                :required="true"
            >
            </x-select>
            <!-- Selection attribute -->
            <x-select
                v-if="selectionAttributes.length > 0"
                :id="'selection-attribute-id'"
                :name="'selection-attribute-name'"
                :text="$t('labelSelectionAttribute')"
                :value="localPropertyValues.selectionAttribute"
                :options="selectionAttributes"
                @selected="$_onSelectionAttribute"
            >
            </x-select>
            <!-- Symbol shift X & Y -->
            <x-number
                :text="$t('labelShiftX')"
                :value="
                    localPropertyValues ? localPropertyValues.symbolShiftX : 0.0
                "
                :id="'symbol-shift-x'"
                :name="'symbol-shift-x'"
                :step="0.01"
                :minValue="-99.99"
                :maxValue="99.99"
                @input="$_onSymbolShiftX"
            >
            </x-number>
            <x-number
                :text="$t('labelShiftY')"
                :value="
                    localPropertyValues ? localPropertyValues.symbolShiftY : 0.0
                "
                :id="'symbol-shift-y'"
                :name="'symbol-shift-y'"
                :step="0.01"
                :minValue="-99.99"
                :maxValue="99.99"
                @input="$_onSymbolShiftY"
            >
            </x-number>
        </div>
        <!-- Style/Matrixset -->
        <div v-if="isOWSLayerWMTSSelected">
            <!-- Style -->
            <x-select
                :id="'selection-style-id'"
                :name="'wmts-style'"
                :text="$t('labelStyle')"
                :value="localPropertyValues.wmtsStyle"
                :required="styleIsRequired"
                :options="comboBoxAvailableStyles"
                @selected="$_onSelectionStyle"
            >
            </x-select>
            <!-- Matrixset -->
            <x-select
                v-if="!isOGCAPI"
                :id="'selection-matrixset-name'"
                :name="'selection-matrixset-name'"
                :text="$t('labelMatrixset')"
                :value="currentMatrixSet"
                :required="isOWSLayerWMTSSelected"
                :options="comboBoxAvailableMatrixSets"
                @selected="$_onSelectionMatrixSet"
            >
            </x-select>
        </div>
        <!-- Imagetype -->
        <div v-if="showSelectImagetype">
            <x-select
                :id="'selection-imagetype-id'"
                :name="'selection-imagetype-name'"
                :text="$t('labelThemeLayerImageType')"
                :required="imageTypeRequired"
                :value="localPropertyValues ? localPropertyValues.format : ''"
                @selected="$_onSelectionImageType"
                :options="comboBoxImageTypes"
            >
            </x-select>
        </div>
        <!-- Description/Keywords -->
        <div v-show="showDescriptionAndKeywords">
            <!-- Description -->
            <x-textarea
                :id="'themelayer-description'"
                :name="'themelayer-description'"
                :text="`${this.$t('labelThemeLayerDescription')}${this.$t(
                    'labelOptional'
                )}`"
                :rows="3"
                v-model="localPropertyValues.description"
            >
            </x-textarea>
            <!-- Source reference/Copyright -->
            <x-textarea
                :id="'themelayerMapTerms'"
                :name="'themelayerMapTerms'"
                :text="`${this.$t('labelCopyright_Reference')}${this.$t(
                    'labelOptional'
                )}`"
                :rows="3"
                v-model="localPropertyValues.mapTerms"
            >
            </x-textarea>
            <!-- Keywords -->
            <x-textarea
                :name="'themelayer-keywords'"
                :text="`${this.$t('labelThemeLayerKeywords')}${this.$t(
                    'labelOptional'
                )}`"
                :rows="2"
                v-model="localPropertyValues.keywords"
            >
            </x-textarea>
            <!-- MetadataUrl -->
            <div class="form-group">
                <x-select
                    :id="'selection-infolinktype-id'"
                    :name="'selection-infolinktype-name'"
                    :text="$t('labelInfoLinkType')"
                    :required="false"
                    :value="localPropertyValues.infoLinkType"
                    @selected="$_onSelectInfoLinkType($event)"
                    :options="$_getInfoLinkTypes()"
                >
                </x-select>
                <div
                    v-if="
                        localPropertyValues.infoLinkType ===
                        INFO_LINK_TYPE.METADATA
                    "
                >
                    <x-input
                        :id="'themelayer-MetadataUrl'"
                        :name="'themelayer-MetadataUrl'"
                        :text="$t('labelThemeLayerMetadataUrl')"
                        :value="localPropertyValues.metadataUrl"
                        @input="$_onInputMetadataUrl($event)"
                        :maxlength="400"
                        :state="localStateValues.metadataUrlValidState"
                        :invalidMessage="metadataUrlInvalidMessage"
                    >
                    </x-input>
                    <b-link @click="$_onSearchMetadataStart" class="mb-1">
                        <em class="fa fa-search pr-1"></em
                        >{{ $t('searchMetadata.search') }}
                    </b-link>
                    <search-metadata
                        v-if="searchMetadataActive"
                        :defaultUrl="defaultUrl"
                        @close="$_onSearchMetadataClose"
                        @updateUrl="$_onUpdateMetadataUrl"
                    />
                </div>
                <div
                    v-if="
                        localPropertyValues.infoLinkType ===
                        INFO_LINK_TYPE.EXTERNAL
                    "
                >
                    <x-input
                        :id="'themelayer-InfoUrl'"
                        :name="'themelayer-InfoUrl'"
                        :text="$t('labelThemeLayerInfoUrl')"
                        :value="localPropertyValues.infoUrl"
                        @input="$_onInputInfoUrl($event)"
                        :maxlength="400"
                        :state="localStateValues.infoUrlValidState"
                        :invalidMessage="infoUrlInvalidMessage"
                    >
                    </x-input>
                </div>
            </div>
        </div>
        <!-- Baselayer -->
        <div v-show="showSelectBaseLayer">
            <x-select
                :id="'selection-baselayer-id'"
                :name="'selection-baselayer-name'"
                :text="$t('labelThemeLayerBaseLayer')"
                :value="baseLayerValue"
                @selected="$_onSelectionBaseLayer"
                :options="comboBoxBaseLayer"
                :required="showSelectBaseLayer"
            >
            </x-select>
        </div>
        <div
            v-if="localPropertyValues.baselayer"
            role="group"
            class="form-row form-group style-max-width"
        >
            <label class="col-form-label font-weight-normal style-label-md2">{{
                $t('scaleRange')
            }}</label>
            <label for="slMinScale" class="col-form-label font-weight-normal"
                >min</label
            >
            <div class="col">
                <x-select
                    id="slMinScale"
                    name="slMinScale"
                    v-model="localPropertyValues.minScale"
                    @selected="$_onSelectMinScale($event)"
                    :options="$_scaleOptions('min')"
                    :simpleMode="true"
                    :showDefaultOption="false"
                >
                </x-select>
            </div>
            <label for="slMaxScale" class="col-form-label font-weight-normal"
                >max</label
            >
            <div class="col">
                <x-select
                    id="slMaxScale"
                    name="slMaxScale"
                    v-model="localPropertyValues.maxScale"
                    @selected="$_onSelectMaxScale($event)"
                    :options="$_scaleOptions('max')"
                    :simpleMode="true"
                    :showDefaultOption="false"
                >
                </x-select>
            </div>
        </div>
        <!-- Baselayer Icon -->
        <div v-if="showBaseLayerIcon" class="image-group">
            <b-form-group
                id="fieldset-1"
                :label="$t('labelThemeLayerIcon')"
                :label-for="'baselayerimage-name'"
                label-class="font-weight-normal"
            >
                <info-button :name="'btn-info'" />
                <tooltip
                    :target="'btn-info'"
                    :title="$t('themelayerImageScale')"
                    :customClass="'xlarge'"
                >
                </tooltip>
                <image-loader
                    name="baselayerimage-name"
                    :value="
                        selectedOwsServiceLayer
                            ? localPropertyValues.baselayerImage
                            : ''
                    "
                    :maxFileSize="maxImageSize"
                    maxFileSizeErrorMessage="100kb"
                    @input="$_onSelectedImage"
                    @loadFileValidState="$_onLoadFileValidState"
                >
                </image-loader>
                <div
                    id="image-preview"
                    v-show="
                        selectedOwsServiceLayer &&
                        localPropertyValues.baselayerImage
                    "
                >
                    <!-- Image preview -->
                    <div id="preview_map_controls" v-show="showIconPreview">
                        <!-- Preview height 60 px-->
                        <img
                            alt="basislaag"
                            id="preview_height"
                            :src="
                                selectedOwsServiceLayer
                                    ? localPropertyValues.baselayerImage
                                    : ''
                            "
                        />
                        <tooltip
                            :target="'preview_height'"
                            :title="$t('scaleVert')"
                        >
                        </tooltip>
                        <a>
                            <span
                                class="fa fa-long-arrow-alt-right"
                                id="preview_arrow"
                            >
                            </span
                        ></a>
                        <!-- Preview height & width 60 px -->
                        <img
                            alt="basislaag"
                            id="preview_height_width"
                            :src="
                                selectedOwsServiceLayer
                                    ? localPropertyValues.baselayerImage
                                    : ''
                            "
                        />
                        <tooltip
                            :target="'preview_height_width'"
                            :title="$t('scaleVertHor')"
                        >
                        </tooltip>
                        <a
                            ><span
                                class="fa fa-long-arrow-alt-right"
                                id="preview_arrow"
                            >
                            </span
                        ></a>
                        <!--Baselayer icon preview-->
                        <a id="preview_map_controls_layers">
                            <img
                                :src="
                                    selectedOwsServiceLayer
                                        ? localPropertyValues.baselayerImage
                                        : ''
                                "
                                width="60"
                                height="60"
                                alt=""
                            />
                        </a>
                        <tooltip
                            :target="'preview_map_controls_layers'"
                            :title="$t('iconPreview')"
                        >
                        </tooltip>
                    </div>
                    <!-- Button show preview -->
                    <b-button
                        variant="primary"
                        v-show="showButtonIconPreview"
                        @click="$_showPreviewImage"
                    >
                        {{ $t('iconShow') }}
                    </b-button>
                    <!-- Button hide preview -->
                    <b-button
                        variant="primary"
                        v-show="!showButtonIconPreview"
                        @click="$_hidePreviewImage"
                    >
                        {{ $t('iconClose') }}
                    </b-button>
                </div>
            </b-form-group>
        </div>
    </div>
</template>

<script>
    import { debounce } from 'lodash';
    import {
        stringNotEmpty,
        arrayNotEmpty,
        convertToValueTextPair,
        validateStringForWordCharactersAndAtLeast3Chars,
        getRefCodes,
        validateUrl
    } from '../../services/utilities';
    import {
        MODE_ADD,
        MODE_EDIT,
        OWS_TYPE,
        INFO_LINK_TYPE,
        NEDGLOBE_SERVICES,
        FEEDBACKS_GEOMETRY_TYPE_CODES
    } from '../../constants';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import XNumber from '../../components/BaseComponents/x-number.vue';
    import XTextArea from '../../components/BaseComponents/x-textarea.vue';
    import ImageLoader from '../../components/ImageLoader.vue';
    import InfoButton from '../../components/InfoButton.vue';
    import Tooltip from '../../components/Tooltip.vue';
    import SearchMetadata from './SearchMetadata.vue';

    export default {
        components: {
            'x-input': XInput,
            'x-select': XSelect,
            'x-checkbox': XCheckbox,
            'x-number': XNumber,
            'x-textarea': XTextArea,
            'image-loader': ImageLoader,
            'info-button': InfoButton,
            tooltip: Tooltip,
            'search-metadata': SearchMetadata
        },

        props: {
            mode: { type: Number, default: 0 },
            selectedOwsService: { type: Object, required: false },
            selectedOwsServiceLayer: { type: Object, required: false },
            conversionToNedGlobeServices: {
                type: Boolean,
                required: false,
                default: false
            },
            isOwsNedGlobeServices: {
                type: Boolean,
                required: false,
                default: false
            },
            isOWSLayerWFSSelected: {
                type: Boolean,
                required: false,
                default: false
            },
            isOWSLayerWMTSSelected: {
                type: Boolean,
                required: false,
                default: false
            },
            selectionAttributes: {
                type: Array,
                required: false,
                default: () => []
            },
            imageFormats: {
                type: Array,
                required: false,
                default: () => []
            },
            isNedGlobeServicesLayerSourceLufo: {
                type: Boolean,
                required: false,
                default: false
            },
            isNedGlobeServicesLayerSourceTable: {
                type: Boolean,
                required: false,
                default: false
            },
            isOGCAPI: {
                type: Boolean,
                required: false,
                default: false
            },
            propertyValues: { type: Object, required: true },
            stateValues: { type: Object, required: true }
        },

        data() {
            return {
                FEEDBACKS_GEOMETRY_TYPE_CODES: FEEDBACKS_GEOMETRY_TYPE_CODES,
                INFO_LINK_TYPE: INFO_LINK_TYPE,

                // local values
                localPropertyValues: null,
                localStateValues: null,

                // tolerance
                toleranceTypes: [
                    {
                        value: '0',
                        text: this.$t('tolerance0mm'),
                        tolerance: 0
                    },
                    {
                        value: '8',
                        text: this.$t('tolerance8mm'),
                        tolerance: 8
                    }
                ],

                // metadata
                metadataUrlInvalidMessage: '',
                infoUrlInvalidMessage: '',
                searchMetadataActive: false,

                // baselayer values
                comboBoxBaseLayer: [
                    {
                        value: 'true',
                        text: this.$t('btnTitleYes')
                    },
                    {
                        value: 'false',
                        text: this.$t('btnTitleNo')
                    }
                ],

                // image
                maxImageSize: 0.1 * 1024 * 1024,
                showIconPreview: false,
                availableScaleItems: null,
                allInfoLinkTypes: []
            };
        },

        async created() {
            const result = await this.$store.dispatch('useApiData', {
                name: 'GetThemeScaleItems'
            });
            this.availableScaleItems = Object.assign([], result.data);
            this.$_resolutionToScale(this.localPropertyValues);
        },

        mounted() {
            this.$_initInfoLinkTypes();
        },

        computed: {
            isEditMode() {
                return (
                    this.mode === MODE_EDIT && !this.conversionToNedGlobeServices
                );
            },
            defaultUrl() {
                if (this.selectedOwsServiceLayer.uri_WMS) {
                    return this.selectedOwsServiceLayer.uri_WMS;
                }
                if (this.selectedOwsServiceLayer.uri_WFS) {
                    return this.selectedOwsServiceLayer.uri_WFS;
                }
                return this.selectedOwsServiceLayer.uri_WMTS;
            },

            geometryTypeArray: {
                get() {
                    return this.localPropertyValues.geometryType
                        ? this.localPropertyValues.geometryType.split('')
                        : [];
                },
                set() {
                    // Vue needs this statement as a setter
                }
            },

            $_hasPointOrLineGeometry(themelayer) {
                return (
                    this.isOwsNedGlobeServices &&
                    (this.localPropertyValues.geometryType?.includes(
                        FEEDBACKS_GEOMETRY_TYPE_CODES.POINT
                    ) ||
                        this.localPropertyValues.geometryType?.includes(
                            FEEDBACKS_GEOMETRY_TYPE_CODES.LINE
                        ))
                );
            },

            isPubliclyAccessible() {
                if (this.selectedOwsServiceLayer) {
                    return this.localPropertyValues.isPubliclyAccessible;
                }
                return false;
            },

            styleIsRequired() {
                return (
                    this.selectedOwsServiceLayer &&
                    arrayNotEmpty(this.selectedOwsServiceLayer.availableStyles) &&
                    stringNotEmpty(this.selectedOwsServiceLayer.availableStyles[0])
                );
            },

            comboBoxAvailableStyles() {
                if (this.selectedOwsServiceLayer?.availableStyles) {
                    // map to b-select format
                    return convertToValueTextPair(
                        this.selectedOwsServiceLayer.availableStyles
                    );
                }
                return [];
            },

            currentMatrixSet() {
                if (
                    this.selectedOwsServiceLayer &&
                    this.localPropertyValues.tileMatrixSet
                ) {
                    return this.localPropertyValues.tileMatrixSet.identifier;
                }
                return null;
            },

            comboBoxAvailableMatrixSets() {
                if (this.selectedOwsServiceLayer?.availableTileMatrixSet) {
                    // map to b-select format
                    return convertToValueTextPair(
                        this.selectedOwsServiceLayer.availableTileMatrixSet,
                        'identifier',
                        'identifier'
                    );
                }
                return [];
            },

            /**
             * Show field Imagetype?
             * Show if OWS is NedGlobe Services and lufo source or
             * Show if layer is WMS or
             * Show if layer is WMTS and tileMatrixSet is selected
             * @return {Boolean} [true if visible, false otherwise]
             */
            showSelectImagetype() {
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceLufo
                ) {
                    return true;
                }
                if (
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMS
                ) {
                    return true;
                }
                return (
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMTS &&
                    this.localPropertyValues.tileMatrixSet
                );
            },

            imageTypeRequired() {
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceLufo
                ) {
                    return true;
                }
                return (
                    this.selectedOwsServiceLayer &&
                    (this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMS ||
                        this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMTS)
                );
            },

            comboBoxImageTypes() {
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceLufo &&
                    !this.isEditMode
                ) {
                    return convertToValueTextPair(this.imageFormats);
                }
                if (this.selectedOwsServiceLayer?.formats) {
                    // filter on 'image/'
                    const filteredItems =
                        this.selectedOwsServiceLayer.formats.filter((item) => {
                            return item.startsWith('image/');
                        });
                    // map to b-select format
                    return convertToValueTextPair(filteredItems);
                }
                return [];
            },

            showDescriptionAndKeywords() {
                if (this.isOGCAPI) {
                    return true;
                }
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceTable
                ) {
                    return true;
                }
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceLufo &&
                    this.localPropertyValues.format
                ) {
                    return true;
                }
                if (
                    this.selectedOwsServiceLayer &&
                    this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WFS
                ) {
                    return true;
                }
                return (
                    this.selectedOwsServiceLayer && this.localPropertyValues.format
                );
            },

            showBaseLayerIcon() {
                return (
                    (this.selectedOwsServiceLayer ||
                        this.isOwsNedGlobeServices ||
                        this.isOGCAPI) &&
                    this.localPropertyValues.baselayer
                );
            },

            showButtonIconPreview() {
                return (
                    this.selectedOwsServiceLayer &&
                    !this.showIconPreview &&
                    (this.localPropertyValues.baselayerImage ||
                        this.selectedOwsServiceLayer.baselayerImageFile)
                );
            },

            // Show if:
            // OWS is NedGlobe Services and lufo source or
            // layer is (WMS or WMTS) and imagetype selected
            showSelectBaseLayer() {
                if (this.isOGCAPI) {
                    return false;
                }
                if (
                    this.isOwsNedGlobeServices &&
                    this.isNedGlobeServicesLayerSourceLufo &&
                    this.localPropertyValues.format !== null
                ) {
                    return true;
                }
                return (
                    this.selectedOwsServiceLayer &&
                    (this.selectedOwsServiceLayer.serviceType === OWS_TYPE.WMS ||
                        this.selectedOwsServiceLayer.serviceType ===
                            OWS_TYPE.WMTS) &&
                    this.localPropertyValues.format !== null
                );
            },

            baseLayerValue() {
                if (this.mode === MODE_ADD) {
                    return null;
                }
                if (
                    this.selectedOwsServiceLayer &&
                    this.localPropertyValues.baselayer
                ) {
                    return 'true';
                }
                return 'false';
            }
        },

        methods: {
            $_initInfoLinkTypes() {
                // Retrieve possible value-input types
                getRefCodes(INFO_LINK_TYPE.KEY).then((result) => {
                    this.allInfoLinkTypes = result.data;
                });
            },

            $_onSelectInfoLinkType(value) {
                this.localPropertyValues.infoLinkType = value;
            },

            $_getInfoLinkTypes() {
                return convertToValueTextPair(
                    this.allInfoLinkTypes,
                    'code',
                    'description'
                );
            },

            $_onIsPubliclyAccessible(value) {
                this.localPropertyValues.isPubliclyAccessible = value;
            },

            $_onSelectMinScale(event) {
                this.localPropertyValues.minScale = event;
                this.$_scaleToResolution(this.localPropertyValues);
            },

            $_onSelectMaxScale(event) {
                this.localPropertyValues.maxScale = event;
                this.$_scaleToResolution(this.localPropertyValues);
            },

            $_scaleToResolution() {
                const max = this.availableScaleItems.filter((scaleItem) => {
                    return scaleItem.scale === this.localPropertyValues.maxScale;
                });

                const min = this.availableScaleItems.filter((scaleItem) => {
                    return scaleItem.scale === this.localPropertyValues.minScale;
                });

                if (arrayNotEmpty(max)) {
                    this.localPropertyValues.maxBaseLayerResolution =
                        max[0].resolution;
                } else {
                    this.localPropertyValues.maxBaseLayerResolution = null;
                }

                if (arrayNotEmpty(min)) {
                    this.localPropertyValues.minBaseLayerResolution =
                        min[0].resolution;
                } else {
                    this.localPropertyValues.minBaseLayerResolution = null;
                }
            },

            $_resolutionToScale() {
                const max = this.availableScaleItems.filter((scale) => {
                    return (
                        scale.resolution ===
                        this.localPropertyValues.maxBaseLayerResolution
                    );
                });

                const min = this.availableScaleItems.filter((scale) => {
                    return (
                        scale.resolution ===
                        this.localPropertyValues.minBaseLayerResolution
                    );
                });

                if (arrayNotEmpty(max)) {
                    this.localPropertyValues.maxScale = max[0].scale;
                }

                if (arrayNotEmpty(min)) {
                    this.localPropertyValues.minScale = min[0].scale;
                }
            },

            $_scaleOptions(minOrMax) {
                if (this.availableScaleItems !== null) {
                    let result = convertToValueTextPair(
                        this.availableScaleItems,
                        'scale',
                        'scale'
                    );

                    if (minOrMax === 'min' && this.localPropertyValues.maxScale) {
                        result = result.filter(
                            (r) => r.value < this.localPropertyValues.maxScale
                        );
                    } else if (
                        minOrMax === 'max' &&
                        this.localPropertyValues.minScale
                    ) {
                        result = result.filter(
                            (r) => r.value > this.localPropertyValues.minScale
                        );
                    }

                    // Add empty option
                    result.unshift({ value: null, text: this.$t('scaleSelect') });
                    return result;
                }
                return [];
            },

            $_onSelectionTolerance(value) {
                const selectedItem = this.toleranceTypes.filter(
                    (item) => item.value === value
                );
                if (arrayNotEmpty(selectedItem)) {
                    this.localPropertyValues.toleranceType = selectedItem[0];
                } else {
                    this.localPropertyValues.toleranceType = null;
                }
            },

            $_onGeometryType(geomTypeArray) {
                // Join the geometry-types to string for storage in database
                this.localPropertyValues.geometryType = geomTypeArray
                    ? geomTypeArray.join('')
                    : '';
                // If no geometrytype the table is empty. Set tolerance to 8mm
                if (!this.localPropertyValues.geometryType) {
                    this.localPropertyValues.toleranceType = this.toleranceTypes[1];
                    // If geometrytype includes point or line set tolerance to 8mm
                } else if (this.$_hasPointOrLineGeometry) {
                    this.localPropertyValues.toleranceType = this.toleranceTypes[1];
                    // if only polygon set tolerance to 0mm
                } else {
                    this.localPropertyValues.toleranceType = this.toleranceTypes[0];
                }
            },

            $_onSelectionAttribute(value) {
                this.localPropertyValues.selectionAttribute = value;
            },

            $_onSymbolShiftX(value) {
                this.localPropertyValues.symbolShiftX = parseFloat(value);
            },

            $_onSymbolShiftY(value) {
                this.localPropertyValues.symbolShiftY = parseFloat(value);
            },

            $_onInputTitle(value) {
                this.localPropertyValues.layerCustomTitle = value;
                this.$emit('checkLayerTitleExists', value);
                if (this.isOwsNedGlobeServices) {
                    this.$emit('changeGraphicLayerTitle', value);
                }
            },

            $_onInputNedGlobeServicesLayerName(value) {
                this.localPropertyValues.layerName = value;

                if (
                    value &&
                    !validateStringForWordCharactersAndAtLeast3Chars(value)
                ) {
                    this.localStateValues.nameInvalidMessage = this.$t(
                        'noSpacesAndAtLeast3Chars'
                    );
                    this.localStateValues.nameValidState = false;
                } else {
                    this.localStateValues.nameInvalidMessage = '';
                    this.localStateValues.nameValidState = true;
                    this.$emit('checkLayerNameExists', value);
                }
            },

            $_onSelectionStyle(value) {
                if (stringNotEmpty(value) && this.selectedOwsServiceLayer) {
                    this.localPropertyValues.wmtsStyle = value;
                }
            },

            $_onSelectionMatrixSet(value) {
                if (stringNotEmpty(value) && this.selectedOwsServiceLayer) {
                    this.localPropertyValues.tileMatrixSet =
                        this.selectedOwsServiceLayer.availableTileMatrixSet.find(
                            (item) => item.identifier === value
                        );
                } else {
                    this.localPropertyValues.tileMatrixSet = null;
                }
            },

            $_onSelectionImageType(value) {
                this.localPropertyValues.format = value;
            },

            $_onInputMetadataUrl: debounce(function (value) {
                this.localPropertyValues.metadataUrl = value;
                this.$_validateMetadataUrl();
            }, 250),

            $_onUpdateMetadataUrl(value) {
                this.localPropertyValues.metadataUrl = value;
                this.$_validateMetadataUrl();
            },

            $_onInputInfoUrl(value) {
                this.localPropertyValues.infoUrl = value;
                this.$_validateInfoUrl();
            },

            async $_validateMetadataUrl() {
                this.localStateValues.metadataUrlValidState = true;
                this.metadataUrlInvalidMessage = null;

                if (
                    this.localPropertyValues.infoLinkType ===
                    INFO_LINK_TYPE.METADATA
                ) {
                    if (!this.localPropertyValues.metadataUrl) {
                        this.localStateValues.metadataUrlValidState = false;
                        this.metadataUrlInvalidMessage = this.$t('requiredField');
                        return;
                    }
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'ValidateMetadataUrl',
                        data: {
                            url: this.localPropertyValues.metadataUrl
                        }
                    });
                    if (!result.data) {
                        this.localStateValues.metadataUrlValidState = false;
                        this.metadataUrlInvalidMessage = this.$t(
                            'searchMetadata.errorInvalidUrl'
                        );
                    }
                }
            },

            $_validateInfoUrl() {
                this.localStateValues.infoUrlValidState = true;
                this.infoUrlInvalidMessage = null;

                if (
                    this.localPropertyValues.infoLinkType ===
                    INFO_LINK_TYPE.EXTERNAL
                ) {
                    if (!this.localPropertyValues.infoUrl) {
                        this.localStateValues.infoUrlValidState = false;
                        this.infoUrlInvalidMessage = this.$t('requiredField');
                    } else if (!validateUrl(this.localPropertyValues.infoUrl)) {
                        this.localStateValues.infoUrlValidState = false;
                        this.infoUrlInvalidMessage = this.$t(
                            'errorUrlConnectionError'
                        );
                    }
                }
            },

            $_onSearchMetadataStart() {
                this.searchMetadataActive = true;
            },

            $_onSearchMetadataClose() {
                this.searchMetadataActive = false;
            },

            $_onSelectionBaseLayer(value) {
                if (value === 'true') {
                    this.localPropertyValues.baselayer = true;
                } else {
                    this.localPropertyValues.baselayer = false;
                    // Reset validation state to valide due to save procedure
                    this.localStateValues.valideImageState = true;
                }
            },

            $_onSelectedImage(value) {
                if (stringNotEmpty(value)) {
                    this.localPropertyValues.baselayerImage = value;
                } else {
                    this.localPropertyValues.baselayerImage = null;
                }
            },

            $_onLoadFileValidState(value) {
                this.localStateValues.valideImageState = value;
            },

            $_showPreviewImage(layer) {
                this.showIconPreview = true;
            },

            $_hidePreviewImage() {
                this.showIconPreview = false;
            },

            async validateProperties() {
                if (
                    this.localPropertyValues.infoLinkType ===
                    INFO_LINK_TYPE.EXTERNAL
                ) {
                    this.localPropertyValues.metadataUrl = '';
                } else if (
                    this.localPropertyValues.infoLinkType ===
                    INFO_LINK_TYPE.METADATA
                ) {
                    this.localPropertyValues.infoUrl = '';
                } else {
                    this.localPropertyValues.infoUrl = '';
                    this.localPropertyValues.metadataUrl = '';
                }
                this.$_validateInfoUrl();
                await this.$_validateMetadataUrl();
                return (
                    this.localStateValues.infoUrlValidState &&
                    this.localStateValues.metadataUrlValidState
                );
            }
        },

        watch: {
            localPropertyValues: {
                handler: function (newVal, oldVal) {
                    if (
                        JSON.stringify(newVal) ===
                        JSON.stringify(this.propertyValues)
                    ) {
                        return;
                    }
                    this.$emit('update', newVal);
                },
                deep: true
            },
            localStateValues: {
                handler: function (newVal) {
                    if (
                        JSON.stringify(newVal) === JSON.stringify(this.stateValues)
                    ) {
                        return;
                    }
                    this.$emit('updateState', newVal);
                },
                deep: true
            },
            propertyValues: {
                handler: async function (newVal, oldVal) {
                    this.localPropertyValues = JSON.parse(JSON.stringify(newVal));

                    // Setting tolerance automatically depends on a change
                    // in the geometry type checkboxes. That won't happen for an empty
                    // table when that is the first table selected in the form
                    // So we catch that situation here.
                    // bugfix #54203 We only need to do this for WFS layers. Otherwise we set
                    // tolerance 8mm for WMS layers.
                    // And we only need to do this for NedGlobe services.

                    if (
                        this.selectedOwsService.Name == NEDGLOBE_SERVICES &&
                        this.isOWSLayerWFSSelected &&
                        !this.localPropertyValues.geometryType
                    ) {
                        this.localPropertyValues.toleranceType =
                            this.toleranceTypes[1];
                    }
                },
                deep: true,
                immediate: true
            },
            stateValues: {
                handler: function (newVal) {
                    this.localStateValues = JSON.parse(JSON.stringify(newVal));
                },
                deep: true,
                immediate: true
            }
        }
    };
</script>

<style scoped>
.error {
    color: red;
}

.image-group {
    margin-bottom: 2em;
}

/* Baselayer icon preview */
#preview_height {
    height: 60px;
}

#preview_height_width {
    height: 60px;
    width: 60px;
}

#preview_map_controls div {
    border: 1px solid #fff;
    display: inline-block;
    box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.75);
    box-sizing: content-box;
}

#preview_map_controls_layers {
    vertical-align: middle;
    cursor: pointer;
    border: 2px #007ac9 solid;
    width: 60px;
    height: 60px;
    display: inline-block;
    box-sizing: content-box;
}

#preview_map_controls img {
    background-position: 0px 0px;
    background-size: 50px;
}

#image-preview {
    margin-top: 25px;
}
</style>
